import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronsUpDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import type { ActivitySubtype, ActivityType, TimeEntryFormData } from '@/types'

type FormErrors = Partial<Record<keyof TimeEntryFormData | 'base', string | string[]>>

interface Props {
  activitySubtypes: ActivitySubtype[]
  activityTypes: ActivityType[]
  data: TimeEntryFormData
  setData: <K extends keyof TimeEntryFormData>(key: K, value: TimeEntryFormData[K]) => void
  errors: FormErrors
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
  submitLabel?: string
  onDelete?: () => void
  autoFocusActivity?: boolean
}

function translateError(code: string, t: (key: string) => string): string {
  const translated = t(`errors.${code}`)
  return translated.startsWith('errors.') ? code : translated
}

function getErrors(
  errors: FormErrors,
  key: keyof FormErrors,
  t: (key: string) => string
): string[] {
  const error = errors[key]
  if (!error) return []
  const codes = Array.isArray(error) ? error : [error]
  return codes.map((code) => translateError(code, t))
}

export function parseDuration(value: string): number | null {
  if (!value) return null

  // Support formats: "1h 30m", "1h30m", "90m", "1.5h", "90"
  const hourMinMatch = value.match(/(\d+)\s*h\s*(\d+)?\s*m?/i)
  if (hourMinMatch) {
    const hours = parseInt(hourMinMatch[1], 10)
    const minutes = parseInt(hourMinMatch[2] || '0', 10)
    return hours * 60 + minutes
  }

  const minMatch = value.match(/^(\d+)\s*m$/i)
  if (minMatch) {
    return parseInt(minMatch[1], 10)
  }

  const hourMatch = value.match(/^(\d+(?:\.\d+)?)\s*h$/i)
  if (hourMatch) {
    return Math.round(parseFloat(hourMatch[1]) * 60)
  }

  // Just a number = minutes
  const numMatch = value.match(/^(\d+)$/)
  if (numMatch) {
    return parseInt(numMatch[1], 10)
  }

  return null
}

export function formatDurationInput(minutes: number | null): string {
  if (!minutes) return ''
  const hours = Math.floor(minutes / 60)
  const remaining = minutes % 60
  if (hours > 0 && remaining > 0) return `${hours}h ${remaining}m`
  if (hours > 0) return `${hours}h`
  return `${remaining}m`
}

export function TimeEntryForm({
  activitySubtypes,
  activityTypes,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  onCancel,
  submitLabel,
  onDelete,
  autoFocusActivity = false,
}: Props) {
  const { t } = useTranslation()
  const hasTimeRange = data.started_at && data.ended_at
  const [inputMode, setInputMode] = useState<'duration' | 'range'>(hasTimeRange ? 'range' : 'duration')
  const [durationInput, setDurationInput] = useState(formatDurationInput(data.duration_minutes))
  const [activityOpen, setActivityOpen] = useState(autoFocusActivity)
  const [previewMinutes, setPreviewMinutes] = useState<number | null>(data.duration_minutes)

  // Track previous activity ids to detect when editing a different entry
  const prevActivityIdRef = useRef({ subtype: data.activity_subtype_id, type: data.activity_type_id })

  // Sync internal state when data changes (e.g., when editing a different entry)
  useEffect(() => {
    // Only sync inputMode when switching to a different entry (activity changed)
    // or on initial mount, not when user is editing the current form
    const isNewEntry =
      prevActivityIdRef.current.subtype !== data.activity_subtype_id ||
      prevActivityIdRef.current.type !== data.activity_type_id
    prevActivityIdRef.current = { subtype: data.activity_subtype_id, type: data.activity_type_id }

    setDurationInput(formatDurationInput(data.duration_minutes))
    setPreviewMinutes(data.duration_minutes)

    if (isNewEntry) {
      setInputMode(data.started_at && data.ended_at ? 'range' : 'duration')
    }
  }, [data.duration_minutes, data.started_at, data.ended_at, data.activity_subtype_id, data.activity_type_id])

  // Debounced preview - parse after 300ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setPreviewMinutes(parseDuration(durationInput))
    }, 300)
    return () => clearTimeout(timer)
  }, [durationInput])

  const handleDurationChange = (value: string) => {
    setDurationInput(value)
  }

  const handleDurationBlur = () => {
    const parsed = parseDuration(durationInput)
    setData('duration_minutes', parsed)
  }

  const handleInputModeChange = (newMode: 'duration' | 'range') => {
    setInputMode(newMode)
    if (newMode === 'duration') {
      // Clear time range when switching to duration
      setData('started_at', null)
      setData('ended_at', null)
    } else {
      // Clear duration when switching to time range
      setData('duration_minutes', null)
      setDurationInput('')
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    // Ensure duration is parsed before submitting (in case user presses Enter without blurring)
    if (inputMode === 'duration') {
      setData('duration_minutes', parseDuration(durationInput))
    }
    onSubmit(e)
  }

  // Group subtypes by activity type
  const groupedSubtypes = activitySubtypes.reduce((acc, subtype) => {
    const typeName = subtype.activity_type.name
    if (!acc[typeName]) {
      acc[typeName] = []
    }
    acc[typeName].push(subtype)
    return acc
  }, {} as Record<string, ActivitySubtype[]>)

  const selectedSubtype = activitySubtypes.find((s) => s.id === data.activity_subtype_id)
  const selectedType = activityTypes.find((t) => t.id === data.activity_type_id)

  // Get display for selected activity
  const getSelectedDisplay = () => {
    if (selectedSubtype) {
      return {
        name: selectedSubtype.full_name,
        color: selectedSubtype.color,
      }
    }
    if (selectedType) {
      return {
        name: selectedType.name,
        color: selectedType.color,
      }
    }
    return null
  }

  const selectedDisplay = getSelectedDisplay()

  const handleSelectSubtype = (subtype: ActivitySubtype) => {
    setData('activity_subtype_id', subtype.id)
    setData('activity_type_id', null)
    setActivityOpen(false)
  }

  const handleSelectType = (type: ActivityType) => {
    setData('activity_type_id', type.id)
    setData('activity_subtype_id', null)
    setActivityOpen(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="activity">{t('timeEntries.activity')}</Label>
        <Popover open={activityOpen} onOpenChange={setActivityOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={activityOpen}
              className="w-full justify-between"
            >
              {selectedDisplay ? (
                <span className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: selectedDisplay.color }}
                  />
                  {selectedDisplay.name}
                </span>
              ) : (
                t('timeEntries.selectActivity')
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
            <Command>
              <CommandInput placeholder={t('timeEntries.searchActivity')} />
              <CommandList>
                <CommandEmpty>{t('timeEntries.noActivityFound')}</CommandEmpty>
                {activityTypes.map((type) => {
                  const subtypes = groupedSubtypes[type.name] || []
                  return (
                    <CommandGroup key={type.id} heading={type.name}>
                      {/* Option to log directly to type */}
                      <CommandItem
                        value={type.name}
                        onSelect={() => handleSelectType(type)}
                      >
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: type.color }}
                        />
                        {type.name}
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4',
                            data.activity_type_id === type.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                      {/* Subtypes */}
                      {subtypes.map((subtype) => (
                        <CommandItem
                          key={subtype.id}
                          value={subtype.full_name}
                          onSelect={() => handleSelectSubtype(subtype)}
                        >
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: subtype.color }}
                          />
                          <span className="pl-2">{subtype.name}</span>
                          <Check
                            className={cn(
                              'ml-auto h-4 w-4',
                              data.activity_subtype_id === subtype.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )
                })}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {getErrors(errors, 'activity_subtype_id', t).map((error, i) => (
          <p key={i} className="text-sm text-destructive">{error}</p>
        ))}
        {getErrors(errors, 'activity_type_id', t).map((error, i) => (
          <p key={i} className="text-sm text-destructive">{error}</p>
        ))}
      </div>

      <div className="space-y-2">
        <Label htmlFor="date">{t('timeEntries.date')}</Label>
        <Input
          id="date"
          type="date"
          value={data.date}
          onChange={(e) => setData('date', e.target.value)}
        />
        {getErrors(errors, 'date', t).map((error, i) => (
          <p key={i} className="text-sm text-destructive">{error}</p>
        ))}
      </div>

      <Tabs value={inputMode} onValueChange={(v) => handleInputModeChange(v as 'duration' | 'range')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="duration">{t('timeEntries.duration')}</TabsTrigger>
          <TabsTrigger value="range">{t('timeEntries.timeRange')}</TabsTrigger>
        </TabsList>
        <TabsContent value="duration" className="space-y-2">
          <Label htmlFor="duration">{t('timeEntries.duration')}</Label>
          <div className="relative">
            <Input
              id="duration"
              placeholder={t('timeEntries.durationPlaceholder')}
              value={durationInput}
              onChange={(e) => handleDurationChange(e.target.value)}
              onBlur={handleDurationBlur}
              className="pr-20"
            />
            {durationInput && (
              <span
                className={cn(
                  'absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none',
                  previewMinutes !== null ? 'text-muted-foreground' : 'text-destructive'
                )}
              >
                {previewMinutes !== null ? `= ${formatDurationInput(previewMinutes)}` : t('timeEntries.invalidDuration')}
              </span>
            )}
          </div>
        </TabsContent>
        <TabsContent value="range" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="started_at">{t('timeEntries.startTime')}</Label>
              <Input
                id="started_at"
                type="time"
                value={data.started_at || ''}
                onChange={(e) => {
                  setData('started_at', e.target.value || null)
                  e.target.blur()
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ended_at">{t('timeEntries.endTime')}</Label>
              <Input
                id="ended_at"
                type="time"
                value={data.ended_at || ''}
                onChange={(e) => {
                  setData('ended_at', e.target.value || null)
                  e.target.blur()
                }}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>
      {getErrors(errors, 'base', t).map((error, i) => (
        <p key={i} className="text-sm text-destructive">{error}</p>
      ))}

      <div className="space-y-2">
        <Label htmlFor="notes">{t('timeEntries.notes')}</Label>
        <Input
          id="notes"
          placeholder={t('timeEntries.notesPlaceholder')}
          value={data.notes || ''}
          onChange={(e) => setData('notes', e.target.value || null)}
        />
      </div>

      <div className="flex gap-3 pt-2">
        {onDelete && (
          <Button type="button" variant="destructive" onClick={onDelete}>
            {t('common.delete')}
          </Button>
        )}
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" className="flex-1" disabled={processing}>
          {submitLabel || t('timeEntries.save')}
        </Button>
      </div>
    </form>
  )
}
